import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { colors, radii } from '../../theme/tokens';
import { Button, Icon } from '../ui';
import { useToast } from '../Toast';
import { tasksApi } from '../../lib/tasksApi';
import { statusMeta, priorityMeta, effectiveFields, daysLeft, checklistProgress, unreadForUser, FieldInput, Avatar } from './taskHelpers';
import useIsDesktop from '../../hooks/useIsDesktop';
import { useAuth } from '../../contexts/AuthContext';
import TaskDetail from './TaskDetail';

const emptyDraft = { taskTypeId: '', title: '', assignees: [], startDate: '', deadline: '', fieldValues: {} };

// Task list for a single department: collapsible rows + inline create form.
const TaskList = ({ department, taskTypes = [], users = [], expandTaskId = null }) => {
  const showToast = useToast();
  const isDesktop = useIsDesktop();
  const { user } = useAuth();
  const myId = user?.id;
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [creating, setCreating] = useState(false);
  // Done tasks are hidden by default; toggle to reveal (they sort to the bottom).
  const [hideDone, setHideDone] = useState(true);
  const [query, setQuery] = useState('');
  const autoExpandedRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await tasksApi.listTasks(department.slug);
      setTasks(data);
    } catch (_) {
      showToast('Failed to load tasks', 'error');
    } finally {
      setLoading(false);
    }
  }, [department.slug, showToast]);

  useEffect(() => {
    load();
    setExpandedId(null);
    setShowCreate(false);
    setDraft(emptyDraft);
  }, [load]);

  const typeById = useMemo(() => {
    const m = {};
    taskTypes.forEach((t) => { m[String(t._id)] = t; });
    return m;
  }, [taskTypes]);

  // Rank users by engagement within THIS department's loaded tasks, so the
  // assignee dropdown surfaces the most-active people first. Cheap heuristic
  // from data we already have: created tasks (x2), assignments, messages sent,
  // and completed checklist items.
  const usersByActivity = useMemo(() => {
    const idOf = (v) => String(v && v._id ? v._id : v || '');
    const score = {};
    const bump = (v, n) => { const k = idOf(v); if (k) score[k] = (score[k] || 0) + n; };
    tasks.forEach((t) => {
      bump(t.createdBy, 2);
      (t.assignees || []).forEach((a) => bump(a, 1));
      (t.messages || []).forEach((m) => bump(m.userId, 1));
      (t.checklist || []).forEach((c) => { if (c.done) bump(c.assignee, 1); });
    });
    return [...users].sort((a, b) => {
      const d = (score[String(b._id)] || 0) - (score[String(a._id)] || 0);
      return d !== 0 ? d : (a.name || '').localeCompare(b.name || '');
    });
  }, [tasks, users]);

  // Task types available in this department (General Task pinned first).
  const availableTypes = useMemo(
    () =>
      taskTypes
        .filter((t) => t.isDefault || !t.departments?.length || t.departments.map(String).includes(String(department._id)))
        .sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0)),
    [taskTypes, department._id]
  );

  const draftType = draft.taskTypeId ? typeById[draft.taskTypeId] : null;
  const draftFields = effectiveFields(draftType, department._id, taskTypes);

  const doneCount = useMemo(() => tasks.filter((t) => t.status === 'done').length, [tasks]);

  // When searching, show ALL matches (done included, so old archived tasks are
  // findable) with done pushed to the bottom. Otherwise respect the hide-done
  // toggle. Either way, done tasks sort last.
  const q = query.trim().toLowerCase();
  const visibleTasks = useMemo(() => {
    let list;
    if (q) {
      list = tasks.filter((t) => {
        if ((t.title || '').toLowerCase().includes(q)) return true;
        return (t.assignees || []).some((a) => (a.name || '').toLowerCase().includes(q));
      });
    } else {
      list = hideDone ? tasks.filter((t) => t.status !== 'done') : tasks;
    }
    return [...list].sort((a, b) => (a.status === 'done' ? 1 : 0) - (b.status === 'done' ? 1 : 0));
  }, [tasks, hideDone, q]);

  const submit = async (e) => {
    e.preventDefault();
    if (!draft.title.trim() || !draft.taskTypeId) {
      showToast('Title and task type are required', 'error');
      return;
    }
    setCreating(true);
    try {
      const created = await tasksApi.createTask({
        taskTypeId: draft.taskTypeId,
        title: draft.title.trim(),
        departments: [department._id],
        assignees: draft.assignees,
        startDate: draft.startDate || null,
        deadline: draft.deadline || null,
        fieldValues: draft.fieldValues
      });
      setTasks((prev) => [created, ...prev]);
      setDraft(emptyDraft);
      setShowCreate(false);
      showToast('Task created', 'success');
    } catch (_) {
      showToast('Failed to create task', 'error');
    } finally {
      setCreating(false);
    }
  };

  const onChanged = (updated) => {
    setTasks((prev) => prev.map((t) => (t._id === updated._id ? updated : t)));
  };

  // Expand/collapse a row. On open, mark the chat read (clears the unread dot)
  // and optimistically update the local read marker so it clears immediately.
  const toggleExpanded = (task) => {
    const opening = expandedId !== task._id;
    setExpandedId(opening ? task._id : null);
    if (opening && myId) {
      const at = new Date().toISOString();
      setTasks((prev) => prev.map((t) => {
        if (t._id !== task._id) return t;
        const reads = (t.reads || []).filter((r) => String(r.userId && r.userId._id ? r.userId._id : r.userId) !== String(myId));
        return { ...t, reads: [...reads, { userId: myId, at }] };
      }));
      tasksApi.markRead(task._id).catch(() => {});
    }
  };
  const onDeleted = (id) => {
    setTasks((prev) => prev.filter((t) => t._id !== id));
    setExpandedId(null);
  };

  // Opened from the dashboard (?task=<id>): auto-expand that task once and mark
  // its chat read so the dashboard update clears on return.
  useEffect(() => {
    if (!expandTaskId || loading) return;
    if (autoExpandedRef.current === expandTaskId) return;
    if (!tasks.some((t) => t._id === expandTaskId)) return;
    autoExpandedRef.current = expandTaskId;
    setExpandedId(expandTaskId);
    if (myId) {
      const at = new Date().toISOString();
      setTasks((prev) => prev.map((t) => {
        if (t._id !== expandTaskId) return t;
        const reads = (t.reads || []).filter((r) => String(r.userId && r.userId._id ? r.userId._id : r.userId) !== String(myId));
        return { ...t, reads: [...reads, { userId: myId, at }] };
      }));
      tasksApi.markRead(expandTaskId).catch(() => {});
    }
  }, [expandTaskId, tasks, loading, myId]);

  const toggleDraftAssignee = (id) =>
    setDraft((d) => ({ ...d, assignees: d.assignees.includes(id) ? d.assignees.filter((x) => x !== id) : [...d.assignees, id] }));

  return (
    <div>
      <div style={styles.toolbar}>
        <h2 style={styles.deptTitle}>
          <Icon name={department.guest ? 'sell' : (department.icon || 'folder')} size={20} color={colors.parGreenBright} /> {department.name}
        </h2>
        <div style={styles.toolbarActions}>
          {doneCount > 0 && (
            <Button
              variant={hideDone ? 'primary' : 'subtle'}
              size="sm"
              icon={hideDone ? 'visibility_off' : 'visibility'}
              onClick={() => setHideDone((v) => !v)}
            >
              {hideDone ? `Show done (${doneCount})` : 'Hide done'}
            </Button>
          )}
          {/* Guests (tagged, not members) can't create tasks in this department. */}
          {!department.guest && (
            <Button
              variant={showCreate ? 'ghost' : 'primary'}
              size="sm"
              icon={showCreate ? 'close' : 'add'}
              onClick={() => setShowCreate((v) => !v)}
            >
              {showCreate ? 'Cancel' : 'New Task'}
            </Button>
          )}
        </div>
      </div>

      {department.guest && (
        <div style={styles.guestNote}>
          <Icon name="visibility" size={16} color={colors.textMuted} />
          <span>You're not a member of {department.name} — showing only tasks you're tagged in.</span>
        </div>
      )}

      {tasks.length > 0 && (
        <div style={styles.searchRow}>
          <Icon name="search" size={18} color={colors.textMuted} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search tasks in ${department.name}…`}
            style={styles.searchInput}
          />
          {query && (
            <button type="button" onClick={() => setQuery('')} style={styles.searchClear} title="Clear search">
              <Icon name="close" size={16} color={colors.textMuted} />
            </button>
          )}
        </div>
      )}

      {showCreate && (
        <form onSubmit={submit} style={styles.createCard}>
          <div style={styles.grid}>
            <div style={styles.field}>
              <label style={styles.label}>Task type *</label>
              <select
                value={draft.taskTypeId}
                onChange={(e) => setDraft((d) => ({ ...d, taskTypeId: e.target.value, fieldValues: {} }))}
                style={styles.input}
              >
                <option value="">— Select type —</option>
                {availableTypes.map((t) => (
                  <option key={t._id} value={t._id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Title *</label>
              <input
                type="text"
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                style={styles.input}
                placeholder="Short task title"
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Start date</label>
              <input type="date" value={draft.startDate} onChange={(e) => setDraft((d) => ({ ...d, startDate: e.target.value }))} style={styles.input} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Deadline</label>
              <input type="date" value={draft.deadline} onChange={(e) => setDraft((d) => ({ ...d, deadline: e.target.value }))} style={styles.input} />
            </div>
          </div>

          {draftFields.length > 0 && (
            <div style={styles.grid}>
              {draftFields.map((f) => (
                <div key={f.key} style={styles.field}>
                  <label style={styles.label}>{f.label}{f.required ? ' *' : ''}</label>
                  <FieldInput
                    field={f}
                    value={draft.fieldValues[f.key]}
                    onChange={(v) => setDraft((d) => ({ ...d, fieldValues: { ...d.fieldValues, [f.key]: v } }))}
                    users={users}
                  />
                </div>
              ))}
            </div>
          )}

          <div style={styles.field}>
            <label style={styles.label}>Assignees</label>
            <div style={styles.assigneeChips}>
              {users.map((u) => {
                const on = draft.assignees.includes(String(u._id));
                return (
                  <button key={u._id} type="button" onClick={() => toggleDraftAssignee(String(u._id))}
                    style={{ ...styles.assigneeChip, ...(on ? styles.assigneeChipOn : {}) }}>
                    <Avatar user={u} size={20} /> {u.name}
                  </button>
                );
              })}
              {users.length === 0 && <span style={styles.muted}>No task-enabled users to assign</span>}
            </div>
          </div>

          <div style={{ marginTop: 4 }}>
            <Button variant="primary" size="sm" icon="add" type="submit" disabled={creating}>
              {creating ? 'Creating…' : 'Create Task'}
            </Button>
          </div>
        </form>
      )}

      {loading ? (
        <div style={styles.loading}>Loading tasks…</div>
      ) : tasks.length === 0 ? (
        <div style={styles.empty}>
          <Icon name="task_alt" size={36} color={colors.textFaint} />
          <p style={{ marginTop: 10 }}>No tasks in {department.name} yet.</p>
        </div>
      ) : visibleTasks.length === 0 ? (
        <div style={styles.empty}>
          <Icon name={q ? 'search_off' : 'task_alt'} size={36} color={colors.textFaint} />
          {q ? (
            <p style={{ marginTop: 10 }}>No tasks match “{query.trim()}”.</p>
          ) : (
            <p style={{ marginTop: 10 }}>All tasks are done. <button type="button" style={styles.linkBtn} onClick={() => setHideDone(false)}>Show done ({doneCount})</button></p>
          )}
        </div>
      ) : (
        <div style={styles.rows}>
          {visibleTasks.map((task) => {
            const meta = statusMeta(task.status);
            const prio = priorityMeta(task.priority);
            const dl = daysLeft(task.deadline);
            const prog = checklistProgress(task);
            const unread = unreadForUser(task, myId);
            const open = expandedId === task._id;
            return (
              <div key={task._id} style={styles.row}>
                <button
                  style={{ ...styles.rowHead, ...(isDesktop ? null : styles.rowHeadMobile) }}
                  onClick={() => toggleExpanded(task)}
                >
                  <Icon name={open ? 'expand_more' : 'chevron_right'} size={20} color={colors.textMuted} />
                  <span style={{ ...styles.rowTitle, ...(isDesktop ? null : styles.rowTitleMobile) }}>{task.title}</span>
                  {isDesktop && (
                    <span style={styles.rowAvatars}>
                      {(task.assignees || []).slice(0, 4).map((a) => (
                        <Avatar key={a._id} user={a} size={24} />
                      ))}
                    </span>
                  )}
                  {prog.total > 0 && (
                    <span
                      style={{ ...styles.progPill, ...(prog.done === prog.total ? styles.progPillDone : null) }}
                      title={`${prog.done} of ${prog.total} checklist items done`}
                    >
                      <Icon name="checklist" size={13} color={prog.done === prog.total ? colors.parGreenBright : colors.textMuted} /> {prog.done}/{prog.total}
                    </span>
                  )}
                  {unread > 0 && (
                    <span
                      style={styles.unreadDot}
                      title={`${unread} unread message${unread === 1 ? '' : 's'} — you're mentioned or assigned`}
                    >
                      {unread > 9 ? '9+' : unread}
                    </span>
                  )}
                  {dl && (
                    <span style={{ ...styles.daysLeft, color: dl.color, borderColor: dl.color }}>
                      <Icon name="event" size={13} color={dl.color} /> {dl.label}
                    </span>
                  )}
                  <span
                    style={{ ...styles.prioDot, background: prio.color }}
                    title={`Priority: ${prio.label}`}
                  />
                  <span style={{ ...styles.statusBadge, color: meta.color, borderColor: meta.color }}>{meta.label}</span>
                </button>
                {open && (
                  <TaskDetail
                    task={task}
                    department={department}
                    taskType={typeById[String(task.taskTypeId)]}
                    allTaskTypes={taskTypes}
                    users={usersByActivity}
                    onChanged={onChanged}
                    onDeleted={onDeleted}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const styles = {
  toolbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap' },
  guestNote: {
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, padding: '8px 12px',
    borderRadius: radii.md, border: `1px solid ${colors.glassBorder}`, background: colors.surfaceSoft,
    color: colors.textMuted, fontSize: 13
  },
  toolbarActions: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  deptTitle: { display: 'flex', alignItems: 'center', gap: 8, margin: 0, fontSize: 18, color: colors.text },
  daysLeft: { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, border: '1px solid', whiteSpace: 'nowrap' },
  progPill: { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, border: `1px solid ${colors.glassBorder}`, color: colors.textMuted, whiteSpace: 'nowrap' },
  progPillDone: { color: colors.parGreenBright, borderColor: colors.parGreen, background: 'rgba(47,182,92,0.12)' },
  unreadDot: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999,
    background: '#f97316', color: '#fff', fontSize: 10.5, fontWeight: 800, lineHeight: 1,
    boxShadow: '0 0 0 2px rgba(249,115,22,0.25)'
  },
  linkBtn: { background: 'none', border: 'none', color: colors.parGreenBright, fontWeight: 700, cursor: 'pointer', padding: 0, fontSize: 'inherit', textDecoration: 'underline' },
  createCard: {
    background: colors.surfaceSoft,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: radii.md,
    padding: 16,
    marginBottom: 16
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 },
  label: { fontSize: 12, fontWeight: 700, color: colors.textMuted },
  input: {
    padding: '10px 12px',
    borderRadius: radii.sm,
    border: `1px solid ${colors.border}`,
    background: colors.inputBg,
    color: colors.text,
    fontSize: 14,
    boxSizing: 'border-box',
    fontFamily: 'inherit'
  },
  assigneeChips: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  assigneeChip: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px',
    borderRadius: radii.pill, border: `1px solid ${colors.glassBorder}`, background: colors.pill,
    color: colors.text, fontSize: 12.5, fontWeight: 600, cursor: 'pointer'
  },
  assigneeChipOn: { background: 'rgba(47,182,92,0.18)', border: '1px solid rgba(47,182,92,0.55)' },
  muted: { color: colors.textFaint, fontSize: 13 },
  loading: { padding: '30px', textAlign: 'center', color: colors.textMuted },
  empty: { padding: '40px 16px', textAlign: 'center', color: colors.textMuted },
  rows: { display: 'flex', flexDirection: 'column', gap: 10 },
  row: { border: `1px solid ${colors.glassBorder}`, borderRadius: radii.md, background: colors.surfaceSoft, overflow: 'hidden' },
  rowHead: {
    display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '12px 14px',
    background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left'
  },
  rowHeadMobile: { gap: 8, padding: '12px 10px' },
  rowTitle: { flex: 1, fontWeight: 700, fontSize: 14.5, color: colors.text, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowTitleMobile: { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip' },
  statusBadge: { fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 999, border: '1px solid', whiteSpace: 'nowrap' },
  prioDot: { width: 14, height: 14, borderRadius: 999, flexShrink: 0, boxShadow: '0 0 0 2px rgba(255,255,255,0.12)' },
  searchRow: {
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
    padding: '8px 12px', borderRadius: radii.md,
    border: `1px solid ${colors.border}`, background: colors.inputBg
  },
  searchInput: {
    flex: 1, border: 'none', outline: 'none', background: 'transparent',
    color: colors.text, fontSize: 14, fontFamily: 'inherit'
  },
  searchClear: { background: 'none', border: 'none', padding: 2, cursor: 'pointer', display: 'inline-flex', borderRadius: radii.sm },
  rowAvatars: { display: 'inline-flex', gap: -2 },
  rowDeadline: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: colors.textMuted, minWidth: 70, justifyContent: 'flex-end' }
};

export default TaskList;
